import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuthorshipSchema, CurrencySchema, MessageSchema, } from './primitives.js';
const c = initContract();
/**
 * Warehouses (MD-08 / INV-04) — every place a quantity can sit: a building,
 * a van, a shop floor, a virtual quarantine shelf.
 *
 * The per-location accounting decisions travel here: valuation method
 * (BR-INV-04-07 — per warehouse, NEVER per company), the negative-stock rule
 * (BR-INV-04-08), sellability (BR-INV-04-06), transferability. The three
 * summary figures (items held / stock value / low count) are COMPUTED from
 * stock_levels on every read — never stored (MD-08 field 23a).
 *
 * Zones and bins: codes unique per warehouse (BR-INV-04-10); a bin joins its
 * zone by IDENTIFIER so renames carry and deletes are refused while bins
 * remain (BR-INV-04-09); switching use_bins off preserves the rows
 * (BR-INV-04-11). Writes carry zones + bins as replace-sets; a bin names its
 * zone by CODE within the payload and the server resolves the identifier.
 *
 * Permission area: `inventory` (PART 16.2.2 row 3).
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const WAREHOUSE_MESSAGES = {
    /** BR-INV-04-01 / BR-MD-08-01. */
    codeAndName: 'Give this location a code and a name.',
    /** BR-MD-08-02 / ERR-MD-08-01. */
    codeTaken: (code, name) => `${code} is already used by ${name}.`,
    /** BR-INV-04-02 / ERR-INV-04-01 — the amber banner, verbatim. */
    cannotDelete: (name) => `This warehouse can't be deleted. ${name} has stock on hand and is referenced by open invoices, quotations or delivery notes. Deactivate it instead to block new transactions while keeping its history intact.`,
    /**
     * BR-MD-08-05 / ERR-MD-08-03 — the MOVEMENT refusal, which is a different
     * rule from the banner above and had been answering with the banner's words.
     *
     * The spec keeps the two apart deliberately. `BR-INV-04-02`/`ERR-INV-04-01`
     * is the amber banner and its sentence is about STOCK ON HAND and document
     * references. `BR-MD-08-05` is "a warehouse with any stock MOVEMENT must not
     * be deleted", and its error row is this sentence — a location emptied years
     * ago still has history, holds nothing, and was being told it "has stock on
     * hand", which is false.
     *
     * IT NAMES THE COUNT, for the reason `codeLocked` does one entry below: "has
     * stock movements" leaves the reader unable to tell one stray test posting
     * from four years of trading, and the remedy differs between those.
     *
     * WORDING DEVIATION, DELIBERATE AND PINNED. The spec's example reads
     * "1,204 movements" with a thousands separator; this renders `1204`, exactly
     * as `codeLocked` already does for the same figure in the same screen's §14
     * table — see the ERR-MD-08-02 pin in `spec-wording.spec.ts`. Two sentences
     * on one screen quoting one count two different ways would be the worse
     * outcome, and the sibling was here first.
     */
    movementsCannotDelete: (name, movements) => `${name} has ${movements} ${movements === 1 ? 'movement' : 'movements'} and cannot be deleted. Switch it off instead.`,
    /** BR-INV-04-05 — an inactive location on any stock action. */
    closed: (name) => `${name} is closed. Pick an open location.`,
    /** BR-MD-08-06 / ERR-MD-08-04 — switching off while stock remains. */
    stillHoldsStock: (name, count) => `${name} still holds ${count} ${count === 1 ? 'item' : 'items'}. Move the stock out before switching it off.`,
    /**
     * BR-MD-08-03 / ERR-MD-08-02 — the once-moved code lock. The sentence is
     * ERR-MD-08-02's verbatim: it NAMES the movement count, because "has stock
     * movements" leaves the reader with no idea whether one test posting or four
     * years of history is what froze the code.
     */
    codeLocked: (name, movements) => `${name} has ${movements} ${movements === 1 ? 'movement' : 'movements'}. Its code cannot change.`,
    /**
     * The once-moved VALUATION lock (BR-MD-08-03's precedent applied to
     * BR-INV-04-07 / N-19). Costing history is written by the method that was
     * in force — FIFO layers exist exactly for the movements posted while the
     * warehouse was FIFO — so switching a live warehouse's method would either
     * rewrite immutable history (R8 forbids it) or leave the layers and the
     * levels contradicting each other. The honest path to a different method
     * is the one the sentence offers, and N-20 already prices it correctly:
     * a transfer values the outgoing move by the source's method and the
     * incoming by the destination's.
     */
    valuationLocked: (name, movements) => `${name} has ${movements} ${movements === 1 ? 'movement' : 'movements'}, costed by its current method. The valuation method is locked once stock has moved — create a location with the method you want and transfer the goods.`,
    /** BR-INV-04-10. */
    zoneDuplicate: (code) => `Zone ${code} already exists in this location.`,
    /** BR-INV-04-09 — deleting a zone that still holds bins. */
    zoneHasBins: (code, bins) => `Zone ${code} still holds ${bins} ${bins === 1 ? 'bin' : 'bins'}. Move or remove them first.`,
    /** A bin naming a zone the payload does not carry. */
    binZoneMissing: (binCode, zoneCode) => `Bin ${binCode} names zone ${zoneCode}, which is not on this warehouse.`,
    /** Bin codes unique per warehouse. */
    binDuplicate: (code) => `Bin ${code} already exists in this location.`,
};
/**
 * D46 — the warehouse family's structured refusals: key + params beside the
 * composed English sentence, so a refusal that names a location, a code or a
 * movement count reaches an Arabic reader in Arabic. One builder per
 * parameterised member the API raises inside an HTTP exception body — the
 * raise sites throw THESE, and each `message` is composed by the
 * `WAREHOUSE_MESSAGES` twin so the two cannot drift (parity pinned member by
 * member in `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * `zoneHasBins` is deliberately NOT here: the API never raises it — the zone
 * delete guard runs client-side, where the screen already speaks ICU
 * (`wh.errZoneHasBins`, pinned in `mfg-inv-arabic-sentences.test.tsx`). A
 * builder for it would be a raise shape with no raise site.
 *
 * The count-bearing members (movementsCannotDelete, stillHoldsStock,
 * codeLocked, valuationLocked) travel the RAW count; the dictionaries
 * pluralise the noun (en mirrors each sentence's own one/other ternary, ar
 * carries the full six-category set).
 */
export const WAREHOUSE_REFUSALS = {
    codeTaken: (code, name) => ({
        message: WAREHOUSE_MESSAGES.codeTaken(code, name),
        key: 'wh.errCodeTaken',
        params: { code, name },
    }),
    cannotDelete: (name) => ({
        message: WAREHOUSE_MESSAGES.cannotDelete(name),
        key: 'wh.errCannotDelete',
        params: { name },
    }),
    movementsCannotDelete: (name, movements) => ({
        message: WAREHOUSE_MESSAGES.movementsCannotDelete(name, movements),
        key: 'wh.errMovementsCannotDelete',
        params: { name, movements },
    }),
    closed: (name) => ({
        message: WAREHOUSE_MESSAGES.closed(name),
        key: 'wh.errClosed',
        params: { name },
    }),
    stillHoldsStock: (name, count) => ({
        message: WAREHOUSE_MESSAGES.stillHoldsStock(name, count),
        key: 'wh.errStillHoldsStock',
        params: { name, count },
    }),
    codeLocked: (name, movements) => ({
        message: WAREHOUSE_MESSAGES.codeLocked(name, movements),
        key: 'wh.errCodeLocked',
        params: { name, movements },
    }),
    valuationLocked: (name, movements) => ({
        message: WAREHOUSE_MESSAGES.valuationLocked(name, movements),
        key: 'wh.errValuationLocked',
        params: { name, movements },
    }),
    zoneDuplicate: (code) => ({
        message: WAREHOUSE_MESSAGES.zoneDuplicate(code),
        key: 'wh.errZoneDuplicate',
        params: { code },
    }),
    binZoneMissing: (binCode, zoneCode) => ({
        message: WAREHOUSE_MESSAGES.binZoneMissing(binCode, zoneCode),
        key: 'wh.errBinZoneMissing',
        params: { binCode, zoneCode },
    }),
    binDuplicate: (code) => ({
        message: WAREHOUSE_MESSAGES.binDuplicate(code),
        key: 'wh.errBinDuplicate',
        params: { code },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const WarehouseTypeSchema = z.enum([
    'main',
    'branch',
    'retail',
    'distribution',
    'virtual',
    'three_pl',
]);
export const ValuationMethodSchema = z.enum(['fifo', 'weighted_average', 'last_cost']);
export const CapacityUnitSchema = z.enum(['pallet', 'sqft', 'sqm']);
export const ZoneTypeSchema = z.enum(['bulk', 'cold', 'pick', 'hazmat', 'other']);
/** A whole-number capacity (MD-08 field 10's decide-at-build: a NUMBER). */
const CapacityStringSchema = z.string().regex(/^\d{1,15}$/, 'Capacity is a whole number.');
export const WarehouseZoneRowSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    name: z.string().nullable(),
    type: ZoneTypeSchema,
    capacity: z.string().nullable(),
    /** How many bins point at this zone (drives the delete refusal). */
    binCount: z.number().int(),
})
    .strict();
export const WarehouseBinRowSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    zoneId: z.uuid().nullable(),
    zoneCode: z.string().nullable(),
    capacity: z.string().nullable(),
})
    .strict();
export const WarehouseSchema = z
    .object({
    id: z.uuid(),
    /** WH-### when Auto allocated it; the stored text either way. */
    code: z.string(),
    name: z.string(),
    type: WarehouseTypeSchema,
    /** MD-08 field 4 — the owning company (Required; a real pick). */
    legalEntityId: z.uuid(),
    /** The entity's current name, joined for display (one record). */
    legalEntityName: z.string().nullable(),
    street: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    postalCode: z.string().nullable(),
    country: z.string().nullable(),
    manager: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    openingHours: z.string().nullable(),
    capacity: z.string().nullable(),
    capacityUnit: CapacityUnitSchema.nullable(),
    currency: z.string(),
    valuationMethod: ValuationMethodSchema,
    allowNegativeStock: z.boolean(),
    includeInAvailable: z.boolean(),
    useBins: z.boolean(),
    carriers: z.string().array(),
    leadTimeDays: z.number().int().nullable(),
    allowTransfers: z.boolean(),
    isDefault: z.boolean(),
    isActive: z.boolean(),
    /** IT-L4: the #rrggbb the Items list tints this place's quantity chip with; null = none. */
    colour: z.string().nullable(),
    zones: WarehouseZoneRowSchema.array(),
    bins: WarehouseBinRowSchema.array(),
    /** MD-08 fields 23–25 — COMPUTED live from stock_levels, never stored. */
    itemsHeld: z.number().int(),
    stockValue: z.string(),
    lowStockCount: z.number().int(),
    /** True once any stock movement names this location (drives the code lock). */
    hasMovements: z.boolean(),
    // MD-RULE-5's six fields — the bare createdAt/updatedAt pair this shape rule-coverage:ignore
    // carried until 20260902100000 is inside the spread.
    ...AuthorshipSchema.shape,
})
    .strict();
const ZoneInputSchema = z
    .object({
    code: z.string().trim().min(1).max(20),
    name: z.string().trim().max(120).nullable().optional(),
    type: ZoneTypeSchema.optional(),
    capacity: CapacityStringSchema.nullable().optional(),
})
    .strict();
const BinInputSchema = z
    .object({
    code: z.string().trim().min(1).max(30),
    /** Resolved against the zones of the SAME write (codes unique per warehouse). */
    zoneCode: z.string().trim().max(20).nullable().optional(),
    capacity: CapacityStringSchema.nullable().optional(),
})
    .strict();
/** IT-L4: `#rrggbb`, lower-case — the same shape a category's colour has. */
export const WarehouseColourSchema = z.string().regex(/^#[0-9a-f]{6}$/);
export const CreateWarehouseRequestSchema = z
    .object({
    /** Absent = Auto: the server allocates WH-### (MD-08 field 1). */
    code: z.string().trim().min(1).max(20).optional(),
    name: z.string().trim().min(1).max(120),
    type: WarehouseTypeSchema.optional(),
    /**
     * MD-08 field 4, Required — but omissible here: with exactly one entity
     * (every tenant starts with one, named after the company) the field is
     * "hidden and set silently"; with more than one the service refuses an
     * omission and asks which company owns the place.
     */
    legalEntityId: z.uuid().optional(),
    street: z.string().trim().max(200).nullable().optional(),
    city: z.string().trim().max(100).nullable().optional(),
    region: z.string().trim().max(100).nullable().optional(),
    postalCode: z.string().trim().max(20).nullable().optional(),
    country: z.string().trim().max(60).nullable().optional(),
    manager: z.string().trim().max(120).nullable().optional(),
    phone: z.string().trim().max(30).nullable().optional(),
    email: z.string().trim().max(200).nullable().optional(),
    openingHours: z.string().trim().max(120).nullable().optional(),
    capacity: CapacityStringSchema.nullable().optional(),
    capacityUnit: CapacityUnitSchema.nullable().optional(),
    currency: CurrencySchema.optional(),
    valuationMethod: ValuationMethodSchema.optional(),
    allowNegativeStock: z.boolean().optional(),
    includeInAvailable: z.boolean().optional(),
    useBins: z.boolean().optional(),
    carriers: z.string().trim().min(1).max(60).array().max(20).optional(),
    leadTimeDays: z.number().int().min(0).max(365).nullable().optional(),
    allowTransfers: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    /** IT-L4: any #rrggbb (lower-case); the six GEN-170 accents are the quick picks. null/absent = none. */
    colour: WarehouseColourSchema.nullable().optional(),
    /** Replace-sets. Bins may name a zone by its code (BR-INV-04-09's join is by id in storage). */
    zones: ZoneInputSchema.array().max(100).optional(),
    bins: BinInputSchema.array().max(500).optional(),
})
    .strict();
export const UpdateWarehouseRequestSchema = CreateWarehouseRequestSchema.partial().extend({
    name: z.string().trim().min(1).max(120).optional(),
});
const warehouseParams = z.object({ warehouseId: z.uuid() }).strict();
export const warehousesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/warehouses',
        query: z
            .object({
            q: z.string().optional(),
            status: z.enum(['active', 'inactive']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ warehouses: WarehouseSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The location master (inventory.view). Summary cards computed live from stock_levels (MD-08 field 23a).',
    },
    get: {
        method: 'GET',
        path: '/api/warehouses/:warehouseId',
        pathParams: warehouseParams,
        responses: { 200: WarehouseSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'One location with its zones, bins and computed stock figures (inventory.view).',
    },
    create: {
        method: 'POST',
        path: '/api/warehouses',
        body: CreateWarehouseRequestSchema,
        responses: {
            201: WarehouseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New location (inventory.create). Code auto-allocated WH-### when absent; defaults per MD-08 (branch, Weighted Average, negative off, sellable on, transfers on).',
    },
    update: {
        method: 'PATCH',
        path: '/api/warehouses/:warehouseId',
        pathParams: warehouseParams,
        body: UpdateWarehouseRequestSchema,
        responses: {
            200: WarehouseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit (inventory.edit). Zones/bins replace when provided; a zone still holding bins cannot vanish (BR-INV-04-09); the code is locked once any movement exists (BR-MD-08-03).',
    },
    deactivate: {
        method: 'POST',
        path: '/api/warehouses/:warehouseId/deactivate',
        pathParams: warehouseParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: WarehouseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Switch off (inventory.edit) — refused while the location holds stock above zero (BR-MD-08-06).',
    },
    reactivate: {
        method: 'POST',
        path: '/api/warehouses/:warehouseId/reactivate',
        pathParams: warehouseParams,
        body: z.object({}).strict().optional(),
        responses: { 200: WarehouseSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Switch back on (inventory.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/warehouses/:warehouseId',
        pathParams: warehouseParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete (inventory.delete) — only a location with no stock and no movements; otherwise the amber banner (BR-INV-04-02) and Deactivate instead.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=warehouses.js.map